import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ActiveFiltersBar, { ACTIVE_FILTERS_BAR_SLOT_ID } from '@/components/UI/ActiveFiltersBar';
import Filters from '@/components/Table/Filters';
import {
  decodeData,
  decodeFiltersFromParams,
  encodeFiltersToParams,
  removePanelFiltersFromSearchParams,
} from '@/utils/encodeUtils';
import { isActiveFilterValue } from '@/modules/search/searchGrid/utils/activeColumnFilters';
import { isSearchTabsEnabled } from '@/config/searchTabs';
import { useSearchTabsStore } from '@/store/searchTabsStore';

type SummaryFilter = {
  name: string;
  operator?: string;
  type?: string;
  value?: unknown;
  emptiness?: string;
  isEvidence?: boolean;
  label?: string;
  integer?: boolean;
  dir?: string;
};

function mergeWithFilterCatalog(decoded: SummaryFilter[]): SummaryFilter[] {
  return decoded.map(filter => {
    const base = Filters.find((candidate: SummaryFilter) => candidate.name === filter.name);
    if (!base) return { ...filter, value: filter.value ?? '' };
    return {
      ...base,
      ...filter,
      type: base.type || filter.type,
      integer: base.integer,
      value: filter.value ?? '',
    };
  });
}

export function readSummaryFiltersFromSearch(search: string): SummaryFilter[] {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  let decoded = decodeFiltersFromParams(params) as SummaryFilter[];

  if (!decoded.length) {
    const filterToken = params.get('f');
    if (filterToken) {
      decoded = (decodeData(filterToken) as SummaryFilter[]) || [];
    }
  }

  const relevant = decoded.filter(filter => filter.isEvidence === undefined || filter.isEvidence === false);

  return mergeWithFilterCatalog(relevant).filter(
    filter => isActiveFilterValue(filter.value) || Boolean(filter.emptiness),
  );
}

function buildSearchWithSummaryFilters(currentSearch: string, filters: SummaryFilter[]): string {
  const params = new URLSearchParams(currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch);
  params.delete('f');
  removePanelFiltersFromSearchParams(params, 'summary');

  const active = filters
    .filter(filter => isActiveFilterValue(filter.value) || Boolean(filter.emptiness))
    .map(filter => ({ ...filter, isEvidence: false }));

  if (active.length > 0) {
    const encoded = encodeFiltersToParams(active);
    for (const [key, value] of encoded.entries()) {
      params.set(key, value);
    }
  }

  const next = params.toString();
  return next ? `?${next}` : '';
}

export function GraphsActiveFiltersBar() {
  const location = useLocation();
  const navigate = useNavigate();

  const filters = useMemo(() => readSummaryFiltersFromSearch(location.search), [location.search]);

  const persistFilters = useCallback(
    (nextFilters: SummaryFilter[]) => {
      const nextSearch = buildSearchWithSummaryFilters(location.search, nextFilters);
      navigate({ search: nextSearch }, { replace: true });
      if (isSearchTabsEnabled) {
        useSearchTabsStore.getState().flushActiveSearch(nextSearch || window.location.search);
      }
    },
    [location.search, navigate],
  );

  const handleRemoveFilter = useCallback(
    (filterToRemove: SummaryFilter) => {
      const next = filters.map(filter => {
        if (
          filter.name === filterToRemove.name &&
          (filter.type !== 'number' || filter.operator === filterToRemove.operator)
        ) {
          return { ...filter, value: '', emptiness: '' };
        }
        return filter;
      });
      persistFilters(next);
    },
    [filters, persistFilters],
  );

  const handleRemoveFilterValue = useCallback(
    (filterName: string, valueToRemove: string) => {
      const next = filters.map(filter => {
        if (filter.name !== filterName || !Array.isArray(filter.value)) return filter;
        const remaining = (filter.value as string[]).filter(
          value => String(value).trim() !== String(valueToRemove).trim(),
        );
        return { ...filter, value: remaining.length > 0 ? remaining : '' };
      });
      persistFilters(next);
    },
    [filters, persistFilters],
  );

  const handleClearAll = useCallback(() => {
    persistFilters([]);
  }, [persistFilters]);

  if (filters.length === 0) return null;

  return (
    <ActiveFiltersBar
      filters={filters}
      onRemoveFilter={handleRemoveFilter}
      onRemoveFilterValue={handleRemoveFilterValue}
      onClearAll={handleClearAll}
      isEvidence={false}
      portalTargetId={ACTIVE_FILTERS_BAR_SLOT_ID}
    />
  );
}
